"use client";

import * as React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowRight, CircleCheck } from "lucide-react";
const services = [{
  id: "item-1",
  title: "1 Introduction to Artificial Intelligence",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597246/img/macro_fabric_texture_01.png",
  description: "Artificial intelligence (AI) has transformed the world of technology, reshaping industries and redefining the way we live, work, and interact. It is a broad field that encompasses many disciplines, including computer science and cognitive science.",
  specs: ["1. Simulates human intelligence in modern computing.", "2. Reshaping industries and redefining daily interaction.", "3. A broad field encompassing computer science and math.", "4. Could our enemies use AI against us? Plenty to learn."],
  link: "#"
}, {
  id: "item-2",
  title: "2. What is Artificial Intelligence?",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597246/img/industrial_dyeing_machinery_02.png",
  description: "At its core, AI is a set of technologies designed to simulate human intelligence, making it the backbone of innovation. It involves algorithms that can learn from data patterns to perform complex tasks autonomously.",
  specs: ["1. Set of technologies simulating human cognition.", "2. The backbone of innovation in modern computing.", "3. Learns from data patterns to solve problems.", "4. Essential for automating complex industrial tasks."],
  link: "#"
}, {
  id: "item-3",
  title: "3. The Four Stages of AI Development",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597246/img/precision_embroidery_needle_03.png",
  description: "Understanding the evolution of AI is crucial. From simple reactive machines to self-aware systems, the journey of artificial intelligence marks the most significant technological shift in human history.",
  specs: ["1. Stage 1: Reactive Machines with no memory.", "2. Stage 2: Limited Memory using historical data.", "3. Stage 3: Theory of Mind understanding emotions.", "4. Stage 4: Self-Awareness and human-level consciousness."],
  link: "#"
}, {
  id: "item-4",
  title: "4. AI Training Models",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597246/img/quality_control_lens_04.png",
  description: "The foundation of any AI system lies in its training methodology. Large Language Models (LLMs) and Generative Pre-trained Transformers (GPT) are currently at the forefront of this digital revolution.",
  specs: ["1. Foundational training using massive datasets.", "2. Large Language Models (LLM) and GPT architectures.", "3. Generative models for text, image, and video.", "4. Reinforcement Learning from Human Feedback (RLHF)."],
  link: "#"
}, {
  id: "item-5",
  title: "5. Types of Machine Learning Models",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597246/img/logistics_conveyor_belt_05.png",
  description: "Machine learning provides the statistical tools to explore and analyze data. By using supervised and unsupervised learning, machines can identify patterns that are invisible to the human eye.",
  specs: ["1. Supervised Learning using labeled data sets.", "2. Unsupervised Learning finding hidden patterns.", "3. Semi-supervised and Reinforcement Learning models.", "4. Deep Learning Networks and predictive analytics."],
  link: "#"
}];
export default function ServiceDetails() {
  return <section data-section-id="5000" id="deep-dive-ai-modules" className="dark bg-[#e0f2f2] text-[#222222] py-16 min-h-screen flex flex-col justify-center font-sans">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="mb-16 border-b border-[#ea0f0f]/10 pb-6">
          <h2 className="text-[12px] font-bold text-[#ea0f0f] uppercase tracking-widest mb-2">
            AI Knowledge Base 
          </h2>
          <h3 className="text-[24px] md:text-[32px] font-bold tracking-tight text-[#ea0f0f]">
            Deep Dive AI Modules
          </h3>
        </div>

        <Accordion type="single" collapsible className="w-full border-t border-transparent">
          {services.map((service, i) => <AccordionItem key={service.id} value={service.id} className="border-none mb-2 data-[state=open]:border-transparent" data-index={i}>
              <AccordionTrigger className="bg-[#fc6e51] px-6 py-4 rounded-[4px] hover:no-underline group transition-all duration-300">
                <span className="text-[19px] font-bold text-white transition-colors duration-300 text-left">
                  {service.title}
                </span>
              </AccordionTrigger>
              
              <AccordionContent className="bg-[#e0f2f2] border-r border-b border-[#e0f2f2] p-6 rounded-b-[4px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start pt-4">
                  {}
                  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[4px] bg-neutral-900">
                    <img src={service.image} alt={`${service.title} detail`} className="object-cover w-full h-full opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-700 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                    <div className="absolute bottom-4 left-4 font-mono text-[10px] text-white/70 bg-[#fc6e51]/50 px-2 py-1 backdrop-blur-sm">
                      AI_MODULE_0{i + 1}.DATA
                    </div>
                  </div>

                  {}
                  <div className="flex flex-col justify-between h-full space-y-8">
                    <div>
                      <h4 className="text-[16px] font-bold text-[#ea0f0f] mb-4 uppercase tracking-wider">
                        Technical Overview
                      </h4>
                      <p className="text-[#222222] text-[16px] leading-relaxed mb-8 max-w-xl">
                        {service.description}
                      </p>
                      
                      <div className="grid grid-cols-1 gap-y-4 border-t border-[#ea0f0f]/10 pt-6">
                        {service.specs.map((spec, idx) => <div key={idx} className="flex items-start gap-3 group/spec">
                            <CircleCheck className="w-4 h-4 text-[#ea0f0f] transition-colors mt-1 shrink-0" />
                            <span className="text-[#222222] text-[16px]">
                              {spec}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    <div className="pt-6">
                      <Button asChild variant="default" className="rounded-[4px] bg-[#fc6e51] text-white hover:bg-[#ea0f0f] hover:text-white transition-all duration-300 uppercase tracking-widest text-[12px] font-bold h-10 px-6">
                        <Link to={service.link}>
                          TOP <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}